import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, Eye, KanbanSquare, List, MonitorPlay, Rows3, Tags } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { BoardDensity } from "./useBoardDensity";
import type { PipelineView } from "./usePipelineScope";

/**
 * Everything about *how* the pipeline is being looked at, behind one control.
 *
 * The header had grown four separate groups — board/list, cards/chips, aggregates, present —
 * eight icon buttons carrying no visible words between them, each explaining itself only on
 * hover. Collapsed into one button here (2026-08-31), which buys the thing icon-only chrome had
 * been paying for: **every option gets a written label**, its icon beside it rather than in
 * place of it.
 *
 * **Aggregates is not in here.** It came in with the rest and went straight back out at the
 * developer's request: it toggles whether a *band of the page* is showing, which is a different
 * kind of thing from choosing how the board draws, and it earns its own button in the row.
 *
 * Scope and creating stay outside it. The year says *which data*, not how it is shown, and an
 * archive year is worth seeing without a click; the creating action never moves, which is a
 * standing rule in [ui-patterns.md](../../../../../.claude/docs/ui-patterns.md).
 *
 * **A menu, not a popover.** Two radio groups and two toggles is exactly what
 * `DropdownMenu` already models — and `MultiSelectFilter` and `RowActions` are built on the same
 * primitive. A popover would have meant a new Radix package to hand-roll keyboard navigation
 * this one already has.
 */
export function ViewOptions({
  view,
  onViewChange,
  density,
  onDensityChange,
  onPresent,
}: {
  view: PipelineView;
  onViewChange: (next: PipelineView) => void;
  density: BoardDensity;
  onDensityChange: (next: BoardDensity) => void;
  /** Entering presentation mode needs the click's own user activation — see `PipelinePage`. */
  onPresent: () => void;
}): React.JSX.Element {
  const t = useT();

  return (
    <DropdownMenu.Root>
      <Tooltip content={t("viewOptions.label")}>
        <DropdownMenu.Trigger asChild>
          <Button variant="outline" size="icon" aria-label={t("viewOptions.label")}>
            <Eye className="h-4 w-4" aria-hidden />
          </Button>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          // z-50: clears the app header, as every other Radix surface in the app does.
          className="bg-popover text-popover-foreground z-50 min-w-56 rounded-md border p-1 shadow-md"
        >
          <Section label={t("view.label")} />
          <DropdownMenu.RadioGroup value={view} onValueChange={(next) => onViewChange(next as PipelineView)}>
            <Choice value="board" label={t("view.board")} icon={KanbanSquare} />
            <Choice value="list" label={t("view.list")} icon={List} />
          </DropdownMenu.RadioGroup>

          {/* Board only. Density means nothing to a table, and a disabled row explaining that
              would be worse than its absence. */}
          {view === "board" && (
            <>
              <DropdownMenu.Separator className="bg-border -mx-1 my-1 h-px" />
              <Section label={t("density.label")} />
              <DropdownMenu.RadioGroup
                value={density}
                onValueChange={(next) => onDensityChange(next as BoardDensity)}
              >
                <Choice value="normal" label={t("density.normal")} icon={Rows3} />
                <Choice value="condensed" label={t("density.condensed")} icon={Tags} />
              </DropdownMenu.RadioGroup>
            </>
          )}

          <DropdownMenu.Separator className="bg-border -mx-1 my-1 h-px" />

          {/* An action, not a state: leaving presentation mode is Esc or its own ✕, never this
              row. The empty span keeps its label on the same left edge as the choices above,
              which have a tick to their left. */}
          <DropdownMenu.Item onSelect={onPresent} className={ITEM}>
            <span className="h-4 w-4 shrink-0" />
            <MonitorPlay className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
            {t("pipeline.fullscreen")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const ITEM =
  "hover:bg-accent data-[highlighted]:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none";

function Section({ label }: { label: string }) {
  return (
    <DropdownMenu.Label className="text-muted-foreground px-2 py-1.5 text-[11px] font-medium tracking-wide uppercase">
      {label}
    </DropdownMenu.Label>
  );
}

/**
 * One option of a radio group: its tick, its icon, its word.
 *
 * The icon is beside the label rather than instead of it — the whole reason these controls left
 * the header was that eight glyphs there named nothing until you hovered them.
 */
function Choice({ value, label, icon: Icon }: { value: string; label: string; icon: LucideIcon }) {
  return (
    <DropdownMenu.RadioItem value={value} className={cn(ITEM, "data-[state=checked]:font-medium")}>
      <Indicator />
      <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
      {label}
    </DropdownMenu.RadioItem>
  );
}

function Indicator() {
  return (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
      <DropdownMenu.ItemIndicator>
        <Check className="h-4 w-4" aria-hidden />
      </DropdownMenu.ItemIndicator>
    </span>
  );
}
